import {
  ChangeEvent,
  Dispatch,
  FormEvent,
  ReactNode,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { useAtomValue } from "jotai";

import BrieChatMessage from "./BrieChatMessage/BrieChatMessage";
import { deleteFileBlobStorage } from "../../utils/FileUtils";
import { UppyFileInterface } from "../Inputs/UppyFile";
import BrieChatEmptyState from "./BrieChatEmptyState";
import { FeedbackMessage } from "./BrieChatFeedback";
import { SearchGPTMessage } from "./GPTConvoTypes";
import BrieChatInput from "./BrieChatInput";
import { IsMobileAtom } from "../../App";

interface BrieChatProps {
  topMessage?: ReactNode;
  messages: SearchGPTMessage[] | FeedbackMessage[];
  isTyping?: boolean;
  disableInput?: boolean;
  inputText: string;
  attachments?: UppyFileInterface[];
  isFeedback?: boolean;
  conversationID: string;
  onSend: (text: string, attachments?: File[]) => void;
  setInputText: Dispatch<SetStateAction<string>>;
  setAttachments?: Dispatch<SetStateAction<UppyFileInterface[]>>;
  onUseVoice?: () => void;
}

function BrieChat({
  topMessage,
  messages,
  isTyping,
  disableInput,
  inputText,
  attachments,
  isFeedback = false,
  conversationID,
  onSend,
  setInputText,
  onUseVoice,
  setAttachments,
}: BrieChatProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useAtomValue(IsMobileAtom);

  // Scroll chat window whenever messages are updated
  useLayoutEffect(() => {
    if (!containerRef.current) return;

    if (messages.length > 0) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    !isMobile && inputRef?.current?.focus?.();
  }, [isMobile, disableInput]);

  return (
    <div className="d-flex flex-column h-100">
      <div
        className="p-3 d-flex flex-column"
        style={{ flexGrow: 1, overflowY: "auto", gap: 10 }}
        ref={containerRef}
      >
        {topMessage && topMessage}
        {messages.length < 1 && !isFeedback && <BrieChatEmptyState />}
        {messages.map((message) => {
          return (
            <BrieChatMessage
              key={message?.id}
              message={message}
              attachments={
                "Attachments" in message ? message?.Attachments : undefined
              }
            />
          );
        })}
        {isTyping && <BrieChatMessage message={undefined} loading={true} />}
      </div>
      <BrieChatInput
        ref={inputRef}
        inputText={inputText}
        onSend={onSend}
        onMessageChange={onMessageChange}
        onUseVoice={onUseVoice}
        sendMessage={sendMessage}
        onUpload={setAttachments ? onUpload : undefined}
        removeAttachment={setAttachments ? removeAttachment : undefined}
        attachments={attachments}
      />
    </div>
  );

  function onMessageChange(e: ChangeEvent<HTMLTextAreaElement>) {
    setInputText(e.target.value);
  }

  function onUpload(files: UppyFileInterface[]) {
    if (!setAttachments || !files?.length) return;

    setAttachments([files[0]]);
  }

  function removeAttachment(file: UppyFileInterface) {
    if (!setAttachments) return;

    const moduleAPIName = isFeedback ? "Feedback" : "BrieGPTChats";
    const recordID =
      conversationID && conversationID !== "new" ? conversationID ?? "" : "";
    const fieldAPIName = isFeedback ? "Messages.Attachments" : "Attachments";

    deleteFileBlobStorage({
      filePath: file.File,
      recordID,
      moduleAPIName: recordID ? moduleAPIName : "",
      fieldAPIName: recordID ? fieldAPIName : "",
      onSuccess: () => {
        setAttachments((prev) => prev.filter((f) => f.id !== file.id));
      },
    });
  }

  async function sendMessage(e: FormEvent | undefined) {
    e && e.preventDefault();

    if (disableInput || inputText?.length < 3) return;

    onSend(inputText);
    setInputText("");
  }
}

export default BrieChat;
